import React, { FC, useEffect, useMemo, useState } from "react";
import {
  TourCardFragment,
  ToursQuery,
  ToursQueryVariables,
} from "@graphql/types";
import { graphql } from "@helpers/graphql";
import { t } from "@helpers/translate";
import ToursBlock from "@components/common/ToursBlock";
import { Spinner } from "@components/ui/Spinner";
import LoadMore from "@components/common/Pagination/LoadMore";
import { getNodes } from "@helpers/getNodes";
import { IBadgeProps } from "../TourPage/auth/SupplierBadges";

type IToursProps = {
  tours?: ToursQuery;
  filters?: ToursQueryVariables;
  badgesAvailable: IBadgeProps[];
};

// we cant use cursor as pagination because has a issue on the order by query creating collision between order by with same data
export const Tours: FC<IToursProps> = ({ tours, filters, badgesAvailable }) => {
  const [page, setPage] = useState(1);

  const [data, setData] = useState<TourCardFragment[]>(
    () => getNodes(tours?.tours) as TourCardFragment[]
  );
  const [isLoading, setIsLoading] = useState(false);

  const lastPage = tours?.tours.pageInfo.totalPages || 1;

  const hasNextPage = useMemo(() => {
    return page < lastPage;
  }, [page, lastPage, tours?.tours.pageInfo.totalPages]);

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    if (page < 2) {
      return;
    }
    setIsLoading(true);
    graphql
      .tours({
        page: page,
        sizePerPage: 6,
        filters: filters?.filters,
      })
      .then((result) => {
        const newData = getNodes(result.tours) as TourCardFragment[];
        setData((oldData) => [...oldData, ...newData]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [page]);

  return (
    <ToursBlock
      title={t({
        id: "supplier.block.tours.title",
        message: "Tours",
      })}
      subtitle={t({
        id: "supplier.block.tours.subtitle",
        message:
          "All tours can be customized for you. Send me a message with your requests!",
      })}
      columns="2"
      showUser={false}
      data={data}
      badgesAvailable={badgesAvailable}
      enableSwipeOnMobile={true}
      hasNextPage={hasNextPage}
      onLoadMore={handleLoadMore}
      isLoading={isLoading}
    >
      {isLoading && (
        <Spinner className="hidden sm:block lg:flex lg:justify-center" />
      )}
      {!isLoading && data.length > 0 && (
        //hide for mobile screens
        <div className="hidden sm:block">
          <LoadMore
            fetchNextPage={() => setPage((page) => page + 1)}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isLoading}
            text={t({
              id: "pagination.button.load-more",
              message: "Load More",
            })}
          />
        </div>
      )}
    </ToursBlock>
  );
};
